/**
 * Utilitários para validação de datas
 */

/**
 * Valida se uma data não é futura e se foi digitada completamente
 * @param {string} data - Data no formato DD/MM/YYYY
 * @returns {boolean} - true se válida (não futura e completa), false se futura ou incompleta
 */
export const validarDataEmissao = (data) => {
  if (!data) return true; // Se não há data, não valida (deixa para outras validações)

  // Verificar se a data está completa (DD/MM/YYYY = 10 caracteres)
  if (data.length !== 10) return false;

  // Verificar se tem formato correto com barras nas posições certas
  if (data.charAt(2) !== "/" || data.charAt(5) !== "/") return false;

  const hoje = new Date();
  hoje.setHours(23, 59, 59, 999); // Fim do dia atual

  // Converter data DD/MM/YYYY para objeto Date
  const partesData = data.split("/");
  if (partesData.length !== 3) return false;

  const [dia, mes, ano] = partesData;

  // Verificar se as partes têm o tamanho correto
  if (dia.length !== 2 || mes.length !== 2 || ano.length !== 4) return false;

  // Verificar se são números válidos
  const diaNum = parseInt(dia);
  const mesNum = parseInt(mes);
  const anoNum = parseInt(ano);

  if (isNaN(diaNum) || isNaN(mesNum) || isNaN(anoNum)) return false;
  if (diaNum < 1 || diaNum > 31) return false;
  if (mesNum < 1 || mesNum > 12) return false;
  if (anoNum < 1900 || anoNum > 2100) return false;

  const dataEmissao = new Date(anoNum, mesNum - 1, diaNum);

  // Verificar se é uma data válida (ex: 31/02 seria inválida)
  if (isNaN(dataEmissao.getTime())) return false;
  if (dataEmissao.getDate() !== diaNum) return false;
  if (dataEmissao.getMonth() !== mesNum - 1) return false;
  if (dataEmissao.getFullYear() !== anoNum) return false;

  // Retornar true se data não é futura
  return dataEmissao <= hoje;
};

/**
 * Valida se uma data não é anterior à data mínima permitida
 * @param {string} data - Data no formato DD/MM/YYYY
 * @param {string} dataMinima - Data mínima no formato DD/MM/YYYY
 * @returns {boolean} - true se válida, false se anterior à mínima
 */
export const validarDataMinima = (data, dataMinima) => {
  if (!data || !dataMinima) return true;

  const [diaData, mesData, anoData] = data.split("/");
  const [diaMinima, mesMinima, anoMinima] = dataMinima.split("/");

  const dataEscolhida = new Date(anoData, mesData - 1, diaData);
  const dataMinPermitida = new Date(anoMinima, mesMinima - 1, diaMinima);

  return dataEscolhida >= dataMinPermitida;
};

/**
 * Converte data DD/MM/YYYY para formato compatível com q-date (YYYY/MM/DD)
 * @param {string} dataString - Data no formato DD/MM/YYYY
 * @returns {string|null} - Data no formato YYYY/MM/DD ou null se inválida
 */
export const converterDataParaQDate = (dataString) => {
  if (!dataString) return null;
  const [dia, mes, ano] = dataString.split("/");
  return `${ano}/${mes.padStart(2, "0")}/${dia.padStart(2, "0")}`;
};

/**
 * Função para usar com :options do q-date, impede seleção de datas futuras
 * @param {string} date - Data no formato YYYY/MM/DD
 * @returns {boolean} - true se data permitida, false se futura
 */
export const opcoesDataCalendario = (date) => {
  const hoje = new Date();
  const dd = String(hoje.getDate()).padStart(2, "0");
  const mm = String(hoje.getMonth() + 1).padStart(2, "0");
  const yyyy = hoje.getFullYear();

  const dataHoje = `${yyyy}/${mm}/${dd}`;

  return date <= dataHoje;
};

/**
 * Formatar data para exibição brasileira
 * @param {string} data - Data em qualquer formato
 * @returns {string} - Data no formato DD/MM/YYYY
 */
export const formatarDataBrasileira = (data) => {
  if (!data) return "";

  const dataObj = new Date(data);
  if (isNaN(dataObj.getTime())) return data; // Retorna original se inválida

  const dia = String(dataObj.getDate()).padStart(2, "0");
  const mes = String(dataObj.getMonth() + 1).padStart(2, "0");
  const ano = dataObj.getFullYear();

  return `${dia}/${mes}/${ano}`;
};
